import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import apiService from '../services/api';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import permissionEventBus from '../utils/permissionEventBus';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isNavigating } = useSessionPersistence();
  const logoutInProgressRef = useRef(false);
  const logoutRef = useRef(null);
  
  // Track when the user last logged in to prevent premature session clearing
  const lastLoginTimeRef = useRef(null);

  // Initialize session on mount
  useEffect(() => {
    const initializeSession = async () => {
      try {
        const userData = localStorage.getItem('user');
        const token = apiService.getAuthToken();
        
        // CRITICAL #1: If React thinks we're authenticated but storage is empty, force logout
        if (isAuthenticated && (!userData || !token)) {
          console.error('❌ CRITICAL: React says authenticated but no storage - forcing immediate logout');
          console.log('🔍 isAuthenticated:', isAuthenticated);
          console.log('🔍 user exists:', !!user);
          console.log('🔍 localStorage user:', !!userData);
          console.log('🔍 token:', !!token);
          
          // Immediately clear React state
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          
          // Clear all storage
          localStorage.clear();
          sessionStorage.clear();
          
          // Force redirect
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
          return;
        }
        
        // CRITICAL #2: Check for session corruption immediately
        // If we're on a protected route but have no user data or token, force redirect
        const currentPath = window.location.pathname;
        const protectedRoutes = ['/tickets', '/dashboard', '/users', '/settings'];
        const isOnProtectedRoute = protectedRoutes.some(route => currentPath.includes(route));
        
        if (isOnProtectedRoute && (!userData || !token)) {
          console.error('❌ CRITICAL: On protected route but no session data - forcing redirect to login');
          window.location.href = window.location.origin + '/login';
          return;
        }
        
        // Reduced logging to prevent console spam
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 SessionContext: Initializing session...');
          console.log('🔍 SessionContext: User data found:', !!userData);
          console.log('🔍 SessionContext: Token found:', !!token);
          
          // Debug: Show all localStorage keys for troubleshooting
          const allKeys = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('user') || key.includes('token') || key.includes('auth'))) {
              allKeys.push(key);
            }
          }
          console.log('🔍 SessionContext: Relevant localStorage keys:', allKeys);
        }
        
        if (!userData || !token) {
          if (process.env.NODE_ENV === 'development') {
            console.log('❌ SessionContext: No user data or token found, attempting recovery...');
          }
          
          // Try to recover from any available data
          let recoveredUser = null;
          let recoveredToken = null;
          
          // Search for user data in user_* keys
          if (!userData) {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('user_') && key !== 'user_') {
                try {
                  const potentialUserData = localStorage.getItem(key);
                  if (potentialUserData) {
                    const user = JSON.parse(potentialUserData);
                    if (user && user.email) {
                      console.log(`✅ SessionContext: Recovered user data from ${key}`);
                      recoveredUser = user;
                      localStorage.setItem('user', potentialUserData);
                      break;
                    }
                  }
                } catch (error) {
                  // Continue searching
                }
              }
            }
          } else {
            try {
              recoveredUser = JSON.parse(userData);
            } catch (error) {
              // Invalid user data
            }
          }
          
          // Search for token in authToken_* keys
          if (!token && recoveredUser) {
            const userSpecificToken = localStorage.getItem(`authToken_${recoveredUser.email}`);
            if (userSpecificToken && apiService.isValidToken(userSpecificToken)) {
              console.log(`✅ SessionContext: Recovered token for ${recoveredUser.email}`);
              recoveredToken = userSpecificToken;
              apiService.setAuthToken(recoveredToken, recoveredUser);
            } else {
              // Search for any valid token
              for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith('authToken_')) {
                  const potentialToken = localStorage.getItem(key);
                  if (potentialToken && apiService.isValidToken(potentialToken)) {
                    console.log(`✅ SessionContext: Recovered token from ${key}`);
                    recoveredToken = potentialToken;
                    apiService.setAuthToken(recoveredToken, recoveredUser);
                    break;
                  }
                }
              }
            }
          }
          
          // If we recovered both user and token, proceed with authentication
          if (recoveredUser && recoveredToken) {
            console.log('✅ SessionContext: Successfully recovered session');
            setUser(recoveredUser);
            setIsAuthenticated(true);
            setIsLoading(false);
            return;
          }
          
          // No recovery possible
          console.log('❌ SessionContext: No recovery possible, clearing session');
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        try {
          const parsedUser = JSON.parse(userData);
          if (!parsedUser || !parsedUser.email) {
            throw new Error('Invalid user data structure');
          }
          
          // Check if user-specific data exists
          const userSpecificData = localStorage.getItem(`user_${parsedUser.email}`);
          if (userSpecificData) {
            const userSpecific = JSON.parse(userSpecificData);
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ SessionContext: Found user-specific data for:', userSpecific.name);
            }
            // Ensure both user and user_${email} keys are set
            localStorage.setItem('user', userSpecificData);
            localStorage.setItem(`user_${parsedUser.email}`, userSpecificData);
            setUser(userSpecific);
            setIsAuthenticated(true);
          } else {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ SessionContext: Found valid user data:', parsedUser.name);
            }
            // Ensure both user and user_${email} keys are set
            localStorage.setItem('user', userData);
            if (parsedUser.email) {
              localStorage.setItem(`user_${parsedUser.email}`, userData);
            }
            setUser(parsedUser);
            setIsAuthenticated(true);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.log('❌ SessionContext: Invalid user data:', error.message);
          }
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ SessionContext: Session initialization failed:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, []); // Only run once on mount
  
  // CRITICAL: Separate effect to detect session corruption
  // This runs ONLY when explicitly needed to avoid infinite loops
  useEffect(() => {
    // Skip if still loading
    if (isLoading) {
      return;
    }
    
    // ONLY check if we're on a protected route
    const currentPath = window.location.pathname;
    const protectedRoutes = ['/tickets', '/dashboard', '/users', '/settings'];
    const isOnProtectedRoute = protectedRoutes.some(route => currentPath.includes(route));
    
    // Skip if not on a protected route
    if (!isOnProtectedRoute) {
      return;
    }
    
    // Skip if not authenticated (normal state on public pages)
    if (!isAuthenticated) {
      return;
    }
    
    // Only now check if authenticated but missing storage (session corruption)
    const userData = localStorage.getItem('user');
    const token = apiService.getAuthToken();
    
    if (!userData || !token) {
      console.error('❌ CRITICAL: Authenticated in React but missing storage on protected route');
      console.log('🔍 isAuthenticated:', isAuthenticated);
      console.log('🔍 user:', user?.email);
      console.log('🔍 localStorage.user:', !!userData);
      console.log('🔍 token:', !!token);
      console.log('🔍 currentPath:', currentPath);
      
      // Force immediate redirect without state updates to avoid infinite loop
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }, [isAuthenticated, user, isLoading]); // Check whenever these change

  // Sync user data to localStorage whenever it changes AND restore token
  // This effect runs whenever user changes AND on mount if user is already set
  useEffect(() => {
    if (user && user.email) {
      const userDataString = JSON.stringify(user);
      // Ensure both keys are always in sync
      localStorage.setItem('user', userDataString);
      localStorage.setItem(`user_${user.email}`, userDataString);
      
      // Also ensure token is stored - try to find it and restore it
      const token = apiService.getAuthToken();
      if (!token) {
        console.error('❌ SessionContext: CRITICAL - User authenticated but no token found');
        console.log('🔍 SessionContext: Attempting token recovery...');
        
        // Token not found - try to find it in any authToken key
        let foundToken = null;
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('authToken_')) {
            const potentialToken = localStorage.getItem(key);
            if (potentialToken && apiService.isValidToken(potentialToken)) {
              foundToken = potentialToken;
              // Found a token, restore it for this user
              localStorage.setItem(`authToken_${user.email}`, foundToken);
              apiService.setAuthToken(foundToken, user);
              console.log(`✅ SessionContext: Successfully restored token for: ${user.email}`);
              break;
            }
          }
        }
        
        // If still no token found and user is authenticated, this is a critical problem
        // Force immediate logout to clear the corrupted state
        if (!foundToken && isAuthenticated && !logoutInProgressRef.current) {
          console.error('❌ SessionContext: CRITICAL SESSION CORRUPTION - No token recovery possible');
          console.log('🔄 SessionContext: Forcing immediate logout to clear corrupted state');
          logoutInProgressRef.current = true;
          
          // Clear all session data immediately
          apiService.clearSession();
          setUser(null);
          setIsAuthenticated(false);
          
          // Force page reload to ensure clean state
          setTimeout(() => {
            console.log('🔄 SessionContext: Reloading page to ensure clean state');
            window.location.reload();
          }, 500);
        }
      } else {
        // Token exists, ensure it's stored for this user
        apiService.setAuthToken(token, user);
        console.log(`✅ SessionContext: Token verified and synced for: ${user.email}`);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔄 SessionContext: Synced user data to localStorage for: ${user.email}`);
      }
    } else if (isAuthenticated && !user && !logoutInProgressRef.current) {
      // User is authenticated but user object is missing - this is inconsistent
      console.error('❌ SessionContext: CRITICAL - isAuthenticated is true but user is null');
      logoutInProgressRef.current = true;
      
      // Clear session immediately
      apiService.clearSession();
      setUser(null);
      setIsAuthenticated(false);
      
      // Force page reload
      setTimeout(() => {
        console.log('🔄 SessionContext: Reloading page due to user/auth mismatch');
        window.location.reload();
      }, 500);
    }
  }, [user, isAuthenticated]);

  // Listen for permission change events
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Create a flag to prevent multiple simultaneous refreshes
    let isRefreshing = false;

    const handlePermissionChange = async (event) => {
      // Prevent multiple simultaneous refreshes
      if (isRefreshing) {
        console.log('⏭️ SessionContext: Refresh already in progress, skipping');
        return;
      }

      const currentUserId = user.id || user._id;
      const currentUserEmail = user.email;
      const eventUserId = event.userId?.toString();
      const currentUserIdStr = currentUserId?.toString();
      const isUpdatedUser = eventUserId === currentUserIdStr;

      console.log('📢 SessionContext: Permission change event received', {
        eventUserId,
        currentUserIdStr,
        currentUserEmail,
        eventType: event.type,
        isUpdatedUser,
        note: 'Will refresh if this is the current user, or refresh anyway to check for changes'
      });

      // Always refresh, but prioritize if it's the current user
      isRefreshing = true;
      console.log('🔄 SessionContext: Immediately refreshing user data on permission change event');

      try {
        // Force a fresh fetch from server (bypass any cache)
        const freshUserData = await apiService.refreshUserData();
        
        if (freshUserData) {
          const updatedUser = {
            ...freshUserData,
            permissions: [...(freshUserData.permissions || [])],
            _refreshKey: Date.now() // Force re-render with timestamp
          };
          
          // Force React to detect change by ensuring new object reference
          setUser(prevUser => {
            // Compare permissions arrays to see if they actually changed
            const prevPerms = prevUser?.permissions || [];
            const newPerms = updatedUser.permissions || [];
            const permsChanged = JSON.stringify(prevPerms.sort()) !== JSON.stringify(newPerms.sort());
            
            if (permsChanged) {
              console.log('✅✅✅ SessionContext: PERMISSIONS CHANGED DETECTED!', {
                oldCount: prevPerms.length,
                newCount: newPerms.length,
                oldPermissions: prevPerms,
                newPermissions: newPerms,
                hadUnassignedTickets: prevPerms.includes('view_unassigned_tickets'),
                hasUnassignedTickets: newPerms.includes('view_unassigned_tickets'),
                hadSystemSettings: prevPerms.includes('manage_system_settings') || prevPerms.includes('edit_system_settings'),
                hasSystemSettings: newPerms.includes('manage_system_settings') || newPerms.includes('edit_system_settings')
              });
            } else {
              console.log('ℹ️ SessionContext: Permissions unchanged in this refresh');
            }
            
            return updatedUser;
          });
          
          // Update localStorage immediately
          if (updatedUser.email) {
            localStorage.setItem('user', JSON.stringify(updatedUser));
            localStorage.setItem(`user_${updatedUser.email}`, JSON.stringify(updatedUser));
            console.log('💾 SessionContext: Updated localStorage with fresh permissions');
          }
          
          console.log('✅ SessionContext: User data refreshed via event', {
            permissionsCount: updatedUser.permissions?.length || 0,
            hasManageSystemSettings: updatedUser.permissions?.includes('manage_system_settings'),
            hasEditSystemSettings: updatedUser.permissions?.includes('edit_system_settings'),
            hasUnassignedTickets: updatedUser.permissions?.includes('view_unassigned_tickets')
          });
        }
      } catch (error) {
        console.error('❌ SessionContext: Error refreshing user data via event:', error);
      } finally {
        isRefreshing = false;
      }
    };

    // Subscribe to permission event bus
    const unsubscribe = permissionEventBus.subscribe(handlePermissionChange);
    
    // Also listen for window custom events (for same-tab communication)
    const handleWindowEvent = (e) => {
      if (e.detail && e.detail.userId) {
        console.log('📢 SessionContext: Window permission update event received:', e.detail);
        handlePermissionChange({ userId: e.detail.userId, type: 'permissions_updated' });
      }
    };
    
    // Also listen for localStorage changes (cross-tab communication)
    const handleStorageChange = (e) => {
      if (e.key === 'permissions_updated') {
        try {
          const data = JSON.parse(e.newValue);
          console.log('📢 SessionContext: localStorage permission update event:', data);
          handlePermissionChange({ userId: data.userId, type: 'permissions_updated' });
        } catch (error) {
          console.error('❌ SessionContext: Error parsing storage event:', error);
        }
      }
    };
    
    window.addEventListener('permissionsUpdated', handleWindowEvent);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      unsubscribe();
      window.removeEventListener('permissionsUpdated', handleWindowEvent);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, user]);

  // Validate session periodically - more aggressive for permission changes
  useEffect(() => {
    if (!isAuthenticated) return;

    // Don't validate on public routes (login, register, etc.)
    const currentPath = window.location.pathname;
    const publicRoutes = ['/login', '/register', '/verify-account', '/forgot-password', '/reset-password'];
    const isOnPublicRoute = publicRoutes.some(route => currentPath.includes(route));
    
    if (isOnPublicRoute) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 SessionContext: On public route, skipping validation');
      }
      return;
    }

    // Don't validate immediately on mount - wait a bit for session to stabilize
    const initialDelay = setTimeout(() => {
      // Validation will run after initial delay
    }, 2000); // Wait 2 seconds after mount before first validation

    const validateSession = async () => {
      try {
        // CRITICAL: Skip validation if user just logged in (within last 15 seconds)
        // This prevents validateSession from clearing the session before storage is fully propagated
        const timeSinceLogin = lastLoginTimeRef.current ? (Date.now() - lastLoginTimeRef.current) : Infinity;
        if (timeSinceLogin < 15000) { // 15 seconds grace period
          console.log(`🔍 SessionContext: Skipping validation - user logged in ${Math.floor(timeSinceLogin / 1000)}s ago (< 15s grace period)`);
          return;
        }
        
        // Skip validation if we're in a navigation scenario
        if (isNavigating()) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 SessionContext: Skipping validation during navigation');
          }
          return;
        }

        // Get current user to make validation user-specific
        const currentUser = localStorage.getItem('user');
        if (!currentUser) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 SessionContext: No current user found, skipping validation');
          }
          return;
        }

        let userEmail = null;
        try {
          const user = JSON.parse(currentUser);
          userEmail = user.email;
        } catch (error) {
          console.error('❌ SessionContext: Error parsing current user:', error);
          return;
        }

        // Always refresh user data to pick up permission changes immediately
        // Don't check cache - always refresh every 30 seconds to get latest permissions
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 SessionContext: Refreshing user data for user: ${userEmail}`);
        }
        
        // First validate session
        const isValid = await apiService.validateSession();
        if (!isValid) {
          // Double-check: don't logout if we still have a token
          const token = apiService.getAuthToken();
          if (!token) {
            if (process.env.NODE_ENV === 'development') {
              console.log(`❌ SessionContext: Session validation failed and no token for user: ${userEmail}, logging out`);
            }
            logout();
          } else {
            // Token exists but validation failed - might be temporary network issue
            console.warn('⚠️ SessionContext: Validation failed but token exists - might be network issue, keeping session');
          }
        } else {
          // Only refresh user data if permissions might have changed (not on every validation)
          // Check if we need to refresh by comparing current permissions with cached permissions
          const cachedUser = JSON.parse(currentUser);
          const currentPermissions = cachedUser.permissions || [];
          
          // Only refresh user data periodically (not every 5 seconds)
          // This prevents unnecessary state updates and API calls
          try {
            // Use a timestamp to track last full refresh
            const lastFullRefresh = localStorage.getItem(`lastFullRefresh_${userEmail}`);
            const now = Date.now();
            const timeSinceLastRefresh = lastFullRefresh ? now - parseInt(lastFullRefresh) : Infinity;
            
            // Only do a full refresh every 30 seconds (or if we don't have a timestamp)
            if (!lastFullRefresh || timeSinceLastRefresh > 30000) {
              const freshUserData = await apiService.refreshUserData();
              if (freshUserData) {
                // Only update if permissions actually changed
                const newPermissions = freshUserData.permissions || [];
                const permissionsChanged = JSON.stringify(currentPermissions.sort()) !== JSON.stringify(newPermissions.sort());
                
                if (permissionsChanged) {
                  // Force state update by creating new object reference
                  const updatedUser = {
                    ...freshUserData,
                    permissions: [...(freshUserData.permissions || [])], // New array reference
                    _refreshKey: Date.now() // Force re-render with timestamp
                  };
                  
                  setUser(updatedUser);
                  
                  // Also update localStorage immediately
                  if (updatedUser.email) {
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    localStorage.setItem(`user_${updatedUser.email}`, JSON.stringify(updatedUser));
                  }
                  
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`✅ SessionContext: User data refreshed for user: ${userEmail} (permissions changed)`);
                  }
                } else {
                  // Permissions didn't change, just update the timestamp
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`✅ SessionContext: User data validated (no changes)`);
                  }
                }
                
                // Update last refresh timestamp
                localStorage.setItem(`lastFullRefresh_${userEmail}`, now.toString());
              }
            } else {
              // Skip refresh if it's been less than 30 seconds
              if (process.env.NODE_ENV === 'development') {
                console.log(`⏭️ SessionContext: Skipping refresh (last refresh was ${Math.round(timeSinceLastRefresh/1000)}s ago)`);
              }
            }
          } catch (error) {
            // Don't fail validation if refresh fails, just log it
            console.error('❌ SessionContext: Error refreshing user data during validation:', error);
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ SessionContext: Session validation successful for user: ${userEmail}`);
          }
        }
      } catch (error) {
        console.error('❌ SessionContext: Session validation error:', error);
        // Don't logout on network errors
      }
    };

    // Validate session every 30 seconds (reduced from 5 seconds to prevent screen flickering)
    // Permission changes will still be picked up via the permissionEventBus
    // But wait for initial delay before starting
    const interval = setTimeout(() => {
      validateSession(); // First run after delay
      const periodicInterval = setInterval(validateSession, 30 * 1000); // Then every 30 seconds
      return () => {
        clearInterval(periodicInterval);
        clearTimeout(initialDelay);
      };
    }, 5000); // Wait 5 seconds after mount before first validation
    
    return () => {
      clearTimeout(interval);
      clearTimeout(initialDelay);
    };
  }, [isAuthenticated, isNavigating]);

  const login = (userData, token) => {
    if (!userData) {
      console.error('❌ SessionContext: userData is undefined or null');
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 SessionContext: Logging in user:', userData?.name || 'Unknown');
    }
    
    // Mark the time of login to prevent validateSession from clearing too early
    lastLoginTimeRef.current = Date.now();
    console.log('✅ Login timestamp set:', lastLoginTimeRef.current);
    
    // Store user data FIRST with user-specific key (ensure both keys are set)
    const userDataString = JSON.stringify(userData);
    localStorage.setItem('user', userDataString);
    if (userData.email) {
      localStorage.setItem(`user_${userData.email}`, userDataString);
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Stored user data for: ${userData.email}`);
    }
    
    // THEN set the token (so it can find the user data) - pass userData to ensure it's stored
    apiService.setAuthToken(token, userData);
    
    // Set user-specific validation time
    apiService.setLastValidation();
    
    // Update state immediately
    setUser(userData);
    setIsAuthenticated(true);
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Login complete for: ${userData.email}`);
    }
  };

  const logout = async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 SessionContext: Logging out user');
    }
    
    // Get current user before clearing session
    const currentUser = localStorage.getItem('user');
    let userEmail = null;
    if (currentUser) {
      try {
        const user = JSON.parse(currentUser);
        userEmail = user.email;
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔍 SessionContext: Logging out user: ${userEmail}`);
        }
      } catch (error) {
        console.error('❌ SessionContext: Error parsing current user during logout:', error);
      }
    }
    
    try {
      // Call logout API to update server-side status
      await apiService.logout();
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Logout API call successful');
      }
    } catch (error) {
      console.error('❌ Logout API call failed:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear session data for this specific user only
    apiService.clearSession();
    setUser(null);
    setIsAuthenticated(false);
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Session cleared successfully for user: ${userEmail || 'unknown'}`);
    }
  };
  
  // Store logout function in ref so it can be accessed by useEffects defined before it
  logoutRef.current = logout;

  // Refresh user data from server
  const refreshUserData = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 SessionContext: Refreshing user data...');
      }
      
      const freshUserData = await apiService.refreshUserData();
      if (freshUserData) {
        // Force state update by creating new object reference
        const updatedUser = {
          ...freshUserData,
          permissions: [...(freshUserData.permissions || [])], // New array reference
          _refreshKey: Date.now() // Force re-render with timestamp
        };
        
        setUser(updatedUser);
        
        // Update localStorage immediately
        if (updatedUser.email) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
          localStorage.setItem(`user_${updatedUser.email}`, JSON.stringify(updatedUser));
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ SessionContext: User data refreshed successfully');
          console.log('📊 SessionContext: Permissions:', updatedUser.permissions?.length || 0);
          console.log('📊 SessionContext: Has manage_system_settings:', updatedUser.permissions?.includes('manage_system_settings'));
        }
        
        return updatedUser;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ SessionContext: Failed to refresh user data');
        }
        return null;
      }
    } catch (error) {
      console.error('❌ SessionContext: Error refreshing user data:', error);
      return null;
    }
  };

  const value = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    refreshUserData
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}; 